import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DashboardWorkbench } from "@/components/dashboard/DashboardWorkbench";
import { MetricCards } from "@/components/dashboard/MetricCards";
import { RecentComplaintsCard } from "@/components/dashboard/RecentComplaintsCard";
import { ComplaintLifecycleCard } from "@/components/dashboard/ComplaintLifecycleCard";
import { ComplaintDetailsPanel } from "@/components/dashboard/ComplaintDetailsPanel";
import {
  createBooth,
  createOrganization,
  createRoleUser,
  getAdminDashboard,
  getComplaintsCsvUrl,
  getComplaintPdfUrl,
  getSupportDashboard,
  getOrgDashboard,
  listBooths,
  listComplaints,
  listOrganizations,
  listRoleUsers,
} from "@/lib/api";

const parseDateLabel = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", { month: "short", day: "2-digit" });
};

const statusClassMap = {
  Created: "sp-pend",
  Acknowledged: "sp-ack",
  InReview: "sp-pend",
  Resolved: "sp-res",
  Archived: "sp-res",
};

export const AdminWorkspacePage = ({ session, mode }) => {
  const [activeNav, setActiveNav] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaintId, setSelectedComplaintId] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [booths, setBooths] = useState([]);
  const [users, setUsers] = useState([]);

  const [orgForm, setOrgForm] = useState({
    organization_name: "",
    head_name: "",
    designation: "",
    address: "",
    email: "",
  });

  const [boothForm, setBoothForm] = useState({
    booth_name: "",
    booth_type: "Physical",
    ownership_type: "Group",
    booth_mode: "Physical",
    organization_id: "",
    address: "",
    incharge_name: "",
    incharge_email: "",
    incharge_mobile_number: "",
    l1_name: "",
    l1_email: "",
    l1_mobile: "",
    l1_sla: "2",
    l2_name: "",
    l2_email: "",
    l2_mobile: "",
    l2_sla: "4",
    l3_name: "",
    l3_email: "",
    l3_mobile: "",
    l3_sla: "6",
  });

  const [userForm, setUserForm] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
    role: "support",
    organization_id: "",
    booth_id: "",
  });

  const [filters, setFilters] = useState({
    status: "",
    level: "",
    urgent: "",
    organization_id: "",
    booth_id: "",
  });

  const workspaceTitle =
    mode === "admin"
      ? "Platform Admin Workspace"
      : mode === "support"
        ? "Support Operations Workspace"
        : "Organization Admin Workspace";

  const navSections = useMemo(() => {
    const opsItems = [{ key: "dashboard", label: "Dashboard", icon: "📊" }, { key: "complaints", label: "Complaints", icon: "📋" }];
    const manageItems = [];
    if (mode === "admin" || mode === "support") {
      manageItems.push(
        { key: "organizations", label: "Organizations", icon: "🏢" },
        { key: "booths", label: "Booths", icon: "🏪" },
        { key: "users", label: "User Management", icon: "👥" },
      );
    }
    return [
      { title: "Operations", items: opsItems },
      { title: "Management", items: manageItems.length ? manageItems : [{ key: "complaints", label: "Complaints", icon: "📋" }] },
    ];
  }, [mode]);

  const loadData = async () => {
    try {
      const dashboardRequest = mode === "admin" ? getAdminDashboard(session.token) : mode === "support" ? getSupportDashboard(session.token) : getOrgDashboard(session.token);

      const [dashboardData, complaintData] = await Promise.all([
        dashboardRequest,
        listComplaints(session.token, {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.level ? { level: filters.level } : {}),
          ...(filters.urgent === "true" ? { urgent: true } : {}),
          ...(filters.urgent === "false" ? { urgent: false } : {}),
          ...(filters.organization_id ? { organization_id: filters.organization_id } : {}),
          ...(filters.booth_id ? { booth_id: filters.booth_id } : {}),
        }),
      ]);

      setDashboard(dashboardData);
      setComplaints(complaintData);

      if (mode === "admin" || mode === "support") {
        const [organizationData, boothData, userData] = await Promise.all([
          listOrganizations(session.token),
          listBooths(session.token),
          listRoleUsers(session.token),
        ]);
        setOrganizations(organizationData);
        setBooths(boothData);
        setUsers(userData);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load workspace data");
    }
  };

  useEffect(() => {
    loadData();
  }, [mode]);

  const metricCards = [
    {
      label: "Total Complaints",
      value: dashboard?.total ?? 0,
      badge: "All Time Records",
      badgeClass: "badge-gray",
    },
    {
      label: "Acknowledged",
      value: dashboard?.acknowledged ?? 0,
      badge: "Reviewed",
      badgeClass: "badge-green",
    },
    {
      label: "Not Acknowledged",
      value: dashboard?.not_acknowledged ?? 0,
      badge: "Awaiting",
      badgeClass: "badge-amber",
    },
    {
      label: "Resolved / Archived",
      value: (dashboard?.resolved ?? 0) + (dashboard?.archived ?? 0),
      badge: "Closed",
      badgeClass: "badge-blue",
    },
  ];

  const recentForCard = complaints.slice(0, 10).map((item) => ({
    ...item,
    statusClass: statusClassMap[item.status] || "sp-pend",
    dateLabel: parseDateLabel(item.created_at),
  }));

  const selectedComplaint = complaints.find((item) => item.complaint_id === selectedComplaintId) || complaints[0] || null;

  const handleDownloadPdf = (complaintId) => {
    if (!complaintId) return;
    window.open(getComplaintPdfUrl(complaintId, session.token), "_blank");
  };

  const handleAuditTrail = () => {
    setActiveNav("complaints");
    toast.info("Audit trail is available in complaint notes and documents panels.");
  };

  const exportCsv = () => {
    window.open(getComplaintsCsvUrl(session.token), "_blank");
  };

  const createOrganizationAction = async () => {
    try {
      await createOrganization(session.token, orgForm);
      toast.success("Organization created and Org Admin invited");
      setOrgForm({ organization_name: "", head_name: "", designation: "", address: "", email: "" });
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Organization create failed");
    }
  };

  const createBoothAction = async () => {
    const payload = {
      booth_name: boothForm.booth_name,
      booth_type: boothForm.booth_type,
      ownership_type: boothForm.ownership_type,
      booth_mode: boothForm.booth_mode,
      organization_id: boothForm.ownership_type === "Group" ? boothForm.organization_id || null : null,
      address: boothForm.address,
      incharge_name: boothForm.incharge_name,
      incharge_email: boothForm.incharge_email,
      incharge_mobile_number: boothForm.incharge_mobile_number,
      escalation_contacts: [
        { level: 1, name: boothForm.l1_name, email: boothForm.l1_email, mobile: boothForm.l1_mobile, sla_days: Number(boothForm.l1_sla || 2) },
        { level: 2, name: boothForm.l2_name, email: boothForm.l2_email, mobile: boothForm.l2_mobile, sla_days: Number(boothForm.l2_sla || 4) },
        { level: 3, name: boothForm.l3_name, email: boothForm.l3_email, mobile: boothForm.l3_mobile, sla_days: Number(boothForm.l3_sla || 6) },
      ],
    };

    try {
      await createBooth(session.token, payload);
      toast.success("Booth created and incharge invited");
      setBoothForm((prev) => ({
        ...prev,
        booth_name: "",
        address: "",
        incharge_name: "",
        incharge_email: "",
        incharge_mobile_number: "",
      }));
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Booth create failed");
    }
  };

  const createRoleUserAction = async () => {
    const payload = {
      ...userForm,
      organization_id: userForm.organization_id || null,
      booth_id: userForm.booth_id || null,
      mobile_number: userForm.mobile_number || null,
    };
    try {
      await createRoleUser(session.token, payload);
      toast.success("Role user created with default password Akku@2507");
      setUserForm({ full_name: "", email: "", mobile_number: "", role: "support", organization_id: "", booth_id: "" });
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "User create failed");
    }
  };

  return (
    <DashboardWorkbench
      session={session}
      workspaceTitle={workspaceTitle}
      navSections={navSections}
      activeKey={activeNav}
      onNavigate={setActiveNav}
      topActions={
        <>
          <Button variant="outline" className="btn-sm" onClick={loadData} data-testid="refresh-dashboard-button">Refresh</Button>
          <Button className="btn-sm btn-sm-blue" onClick={exportCsv} data-testid="export-csv-button">Export List</Button>
        </>
      }
    >
      {activeNav === "dashboard" ? (
        <>
          <MetricCards cards={metricCards} />

          <div className="two-col" data-testid="dashboard-two-col">
            <RecentComplaintsCard
              title="Recent Lodged Records"
              complaints={recentForCard}
              onOpen={(complaintId) => {
                setSelectedComplaintId(complaintId);
                setActiveNav("complaints");
              }}
            />

            <ComplaintLifecycleCard
              complaint={selectedComplaint}
              onDownloadPdf={handleDownloadPdf}
              onAuditTrail={handleAuditTrail}
            />
          </div>
        </>
      ) : null}

      {activeNav === "complaints" ? (
        <>
          <div className="card" data-testid="complaint-filter-card">
            <div className="card-head">
              <h3>Complaint Filters</h3>
            </div>
            <div className="card-body filter-grid">
              <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} data-testid="filter-status-select">
                <option value="">All Status</option>
                <option value="Created">Created</option>
                <option value="Acknowledged">Acknowledged</option>
                <option value="InReview">InReview</option>
                <option value="Resolved">Resolved</option>
                <option value="Archived">Archived</option>
              </select>
              <select value={filters.level} onChange={(event) => setFilters((prev) => ({ ...prev, level: event.target.value }))} data-testid="filter-level-select">
                <option value="">All Levels</option>
                <option value="1">Level 1</option>
                <option value="2">Level 2</option>
                <option value="3">Level 3</option>
              </select>
              <select value={filters.urgent} onChange={(event) => setFilters((prev) => ({ ...prev, urgent: event.target.value }))} data-testid="filter-urgent-select">
                <option value="">Urgency: All</option>
                <option value="true">Urgent</option>
                <option value="false">Not urgent</option>
              </select>

              {(mode === "admin" || mode === "support") ? (
                <>
                  <select value={filters.organization_id} onChange={(event) => setFilters((prev) => ({ ...prev, organization_id: event.target.value }))} data-testid="filter-organization-select">
                    <option value="">All Organizations</option>
                    {organizations.map((org) => (
                      <option key={org.organization_id} value={org.organization_id}>{org.organization_name}</option>
                    ))}
                  </select>
                  <select value={filters.booth_id} onChange={(event) => setFilters((prev) => ({ ...prev, booth_id: event.target.value }))} data-testid="filter-booth-select">
                    <option value="">All Booths</option>
                    {booths.map((booth) => (
                      <option key={booth.booth_id} value={booth.booth_id}>{booth.booth_name}</option>
                    ))}
                  </select>
                </>
              ) : null}

              <Button onClick={loadData} data-testid="apply-filters-button">Apply Filters</Button>
            </div>
          </div>

          <div className="two-col" data-testid="complaint-detail-layout">
            <RecentComplaintsCard
              title="Filtered Complaints"
              complaints={recentForCard}
              onOpen={(complaintId) => setSelectedComplaintId(complaintId)}
            />
            <ComplaintDetailsPanel
              token={session.token}
              complaintId={selectedComplaintId || recentForCard[0]?.complaint_id}
              role={session.role}
              onUpdated={loadData}
            />
          </div>
        </>
      ) : null}

      {(activeNav === "organizations" && (mode === "admin" || mode === "support")) ? (
        <>
          <div className="card" data-testid="organization-create-card">
            <div className="card-head">
              <h3>Onboard Organization</h3>
            </div>
            <div className="card-body form-grid">
              <input placeholder="Organization Name" value={orgForm.organization_name} onChange={(event) => setOrgForm((prev) => ({ ...prev, organization_name: event.target.value }))} data-testid="org-name-input" />
              <input placeholder="Head Name" value={orgForm.head_name} onChange={(event) => setOrgForm((prev) => ({ ...prev, head_name: event.target.value }))} data-testid="org-head-input" />
              <input placeholder="Designation" value={orgForm.designation} onChange={(event) => setOrgForm((prev) => ({ ...prev, designation: event.target.value }))} data-testid="org-designation-input" />
              <input placeholder="Address" value={orgForm.address} onChange={(event) => setOrgForm((prev) => ({ ...prev, address: event.target.value }))} data-testid="org-address-input" />
              <input placeholder="Email" value={orgForm.email} onChange={(event) => setOrgForm((prev) => ({ ...prev, email: event.target.value }))} data-testid="org-email-input" />
              <Button onClick={createOrganizationAction} data-testid="create-organization-button">Create Organization</Button>
            </div>
          </div>

          <div className="card" data-testid="organization-list-card">
            <div className="card-head">
              <h3>Organization List</h3>
            </div>
            <div className="card-body card-body-flat">
              {organizations.map((org) => (
                <div className="complaint-row" key={org.organization_id} data-testid={`organization-row-${org.organization_id}`}>
                  <span className="comp-id">{org.organization_id}</span>
                  <span className="comp-title">{org.organization_name}</span>
                  <span className="status-pill sp-ack">{org.status}</span>
                  <span className="comp-date">{org.email}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {(activeNav === "booths" && (mode === "admin" || mode === "support")) ? (
        <>
          <div className="card" data-testid="booth-create-card">
            <div className="card-head">
              <h3>Create Booth</h3>
            </div>
            <div className="card-body form-grid">
              <input placeholder="Booth Name" value={boothForm.booth_name} onChange={(event) => setBoothForm((prev) => ({ ...prev, booth_name: event.target.value }))} data-testid="booth-name-input" />
              <select value={boothForm.booth_type} onChange={(event) => setBoothForm((prev) => ({ ...prev, booth_type: event.target.value }))} data-testid="booth-type-select">
                <option value="Physical">Physical</option>
                <option value="Online">Online</option>
              </select>
              <select value={boothForm.ownership_type} onChange={(event) => setBoothForm((prev) => ({ ...prev, ownership_type: event.target.value }))} data-testid="booth-ownership-select">
                <option value="Group">Group</option>
                <option value="Independent">Independent</option>
              </select>
              <select value={boothForm.booth_mode} onChange={(event) => setBoothForm((prev) => ({ ...prev, booth_mode: event.target.value }))} data-testid="booth-mode-select">
                <option value="Physical">Physical</option>
                <option value="Online">Online</option>
              </select>
              <select value={boothForm.organization_id} onChange={(event) => setBoothForm((prev) => ({ ...prev, organization_id: event.target.value }))} data-testid="booth-organization-select">
                <option value="">Select Organization (optional for independent)</option>
                {organizations.map((org) => (
                  <option value={org.organization_id} key={org.organization_id}>{org.organization_name}</option>
                ))}
              </select>
              <input placeholder="Address" value={boothForm.address} onChange={(event) => setBoothForm((prev) => ({ ...prev, address: event.target.value }))} data-testid="booth-address-input" />
              <input placeholder="Incharge Name" value={boothForm.incharge_name} onChange={(event) => setBoothForm((prev) => ({ ...prev, incharge_name: event.target.value }))} data-testid="booth-incharge-name-input" />
              <input placeholder="Incharge Email" value={boothForm.incharge_email} onChange={(event) => setBoothForm((prev) => ({ ...prev, incharge_email: event.target.value }))} data-testid="booth-incharge-email-input" />
              <input placeholder="Incharge Mobile" value={boothForm.incharge_mobile_number} onChange={(event) => setBoothForm((prev) => ({ ...prev, incharge_mobile_number: event.target.value }))} data-testid="booth-incharge-mobile-input" />

              <input placeholder="L1 Name" value={boothForm.l1_name} onChange={(event) => setBoothForm((prev) => ({ ...prev, l1_name: event.target.value }))} data-testid="booth-l1-name-input" />
              <input placeholder="L1 Email" value={boothForm.l1_email} onChange={(event) => setBoothForm((prev) => ({ ...prev, l1_email: event.target.value }))} data-testid="booth-l1-email-input" />
              <input placeholder="L1 Mobile" value={boothForm.l1_mobile} onChange={(event) => setBoothForm((prev) => ({ ...prev, l1_mobile: event.target.value }))} data-testid="booth-l1-mobile-input" />
              <input placeholder="L1 SLA Days" value={boothForm.l1_sla} onChange={(event) => setBoothForm((prev) => ({ ...prev, l1_sla: event.target.value }))} data-testid="booth-l1-sla-input" />

              <input placeholder="L2 Name" value={boothForm.l2_name} onChange={(event) => setBoothForm((prev) => ({ ...prev, l2_name: event.target.value }))} data-testid="booth-l2-name-input" />
              <input placeholder="L2 Email" value={boothForm.l2_email} onChange={(event) => setBoothForm((prev) => ({ ...prev, l2_email: event.target.value }))} data-testid="booth-l2-email-input" />
              <input placeholder="L2 Mobile" value={boothForm.l2_mobile} onChange={(event) => setBoothForm((prev) => ({ ...prev, l2_mobile: event.target.value }))} data-testid="booth-l2-mobile-input" />
              <input placeholder="L2 SLA Days" value={boothForm.l2_sla} onChange={(event) => setBoothForm((prev) => ({ ...prev, l2_sla: event.target.value }))} data-testid="booth-l2-sla-input" />

              <input placeholder="L3 Name" value={boothForm.l3_name} onChange={(event) => setBoothForm((prev) => ({ ...prev, l3_name: event.target.value }))} data-testid="booth-l3-name-input" />
              <input placeholder="L3 Email" value={boothForm.l3_email} onChange={(event) => setBoothForm((prev) => ({ ...prev, l3_email: event.target.value }))} data-testid="booth-l3-email-input" />
              <input placeholder="L3 Mobile" value={boothForm.l3_mobile} onChange={(event) => setBoothForm((prev) => ({ ...prev, l3_mobile: event.target.value }))} data-testid="booth-l3-mobile-input" />
              <input placeholder="L3 SLA Days" value={boothForm.l3_sla} onChange={(event) => setBoothForm((prev) => ({ ...prev, l3_sla: event.target.value }))} data-testid="booth-l3-sla-input" />

              <Button onClick={createBoothAction} data-testid="create-booth-button">Create Booth</Button>
            </div>
          </div>

          <div className="card" data-testid="booth-list-card">
            <div className="card-head">
              <h3>Booth List</h3>
            </div>
            <div className="card-body card-body-flat">
              {booths.map((booth) => (
                <div className="complaint-row" key={booth.booth_id} data-testid={`booth-row-${booth.booth_id}`}>
                  <span className="comp-id">{booth.booth_id}</span>
                  <span className="comp-title">{booth.booth_name}</span>
                  <span className="status-pill sp-ack">{booth.status}</span>
                  <span className="comp-date">{booth.incharge_name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {(activeNav === "users" && (mode === "admin" || mode === "support")) ? (
        <>
          <div className="card" data-testid="user-create-card">
            <div className="card-head">
              <h3>Create Support/Org/Booth User</h3>
            </div>
            <div className="card-body form-grid">
              <input placeholder="Full Name" value={userForm.full_name} onChange={(event) => setUserForm((prev) => ({ ...prev, full_name: event.target.value }))} data-testid="user-full-name-input" />
              <input placeholder="Email" value={userForm.email} onChange={(event) => setUserForm((prev) => ({ ...prev, email: event.target.value }))} data-testid="user-email-input" />
              <input placeholder="Mobile Number" value={userForm.mobile_number} onChange={(event) => setUserForm((prev) => ({ ...prev, mobile_number: event.target.value }))} data-testid="user-mobile-input" />
              <select value={userForm.role} onChange={(event) => setUserForm((prev) => ({ ...prev, role: event.target.value }))} data-testid="user-role-select">
                <option value="support">support</option>
                <option value="org_admin">org_admin</option>
                <option value="booth_incharge">booth_incharge</option>
              </select>
              <select value={userForm.organization_id} onChange={(event) => setUserForm((prev) => ({ ...prev, organization_id: event.target.value }))} data-testid="user-organization-select">
                <option value="">Organization (optional)</option>
                {organizations.map((org) => (
                  <option key={org.organization_id} value={org.organization_id}>{org.organization_name}</option>
                ))}
              </select>
              <select value={userForm.booth_id} onChange={(event) => setUserForm((prev) => ({ ...prev, booth_id: event.target.value }))} data-testid="user-booth-select">
                <option value="">Booth (optional)</option>
                {booths.map((booth) => (
                  <option key={booth.booth_id} value={booth.booth_id}>{booth.booth_name}</option>
                ))}
              </select>
              <Button onClick={createRoleUserAction} data-testid="create-role-user-button">Create Role User</Button>
            </div>
          </div>

          <div className="card" data-testid="user-list-card">
            <div className="card-head">
              <h3>User List</h3>
            </div>
            <div className="card-body card-body-flat">
              {users.map((user) => (
                <div className="complaint-row" key={user.id} data-testid={`role-user-row-${user.id}`}>
                  <span className="comp-id">{user.role}</span>
                  <span className="comp-title">{user.full_name}</span>
                  <span className="status-pill sp-ack">{user.identifier || user.email}</span>
                  <span className="comp-date">Default: Akku@2507</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </DashboardWorkbench>
  );
};
